import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useReviewSystemV3Experiment from '@src/quince-site-common/src/hooks/useReviewSystemV3Experiment'
import { IFilterOption } from '@src/quince-site-pdp/src/organisms/productPageSections/productReviews/types'
import { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { Chip } from '../../chip'
import { FilterDrawer } from '../../filterSection/components/FilterDrawer'
import { RadioButtonGroup } from '../../radioButtonGroup'
import { ReviewFilterTags } from '../reviewFilterTags/ReviewFilterTags'

import styles from './allReviews.module.scss'
import CrossIcon from './icons/Cross.inline.svg'
import Select, { SelectOptionProps } from './Select'

export interface ReviewsFilterProps {
  sortByOptions: SelectOptionProps[]
  sortBy: string
  setSortBy: (val: string) => void
  onSearchReview: (val: string) => void
  setHasPhoto: (val: boolean) => void
  hasPhoto: boolean
  className?: string
  variants?: any
  productProperties?: ProductProperties
  newPdpv2?: boolean
  filterOptions: IFilterOption[]
  setFilterBy: (filterBy: IFilterOption) => void
  filterBy: IFilterOption
  setPage: (page: number) => void
  reviewSystemExp: ReturnType<typeof useReviewSystemV3Experiment>
  totalReviewsCount: number
}

export const ReviewsFilter = ({ className = '', reviewSystemExp, ...props }: ReviewsFilterProps) => {
  const { t } = useTranslation('common')
  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false)
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const isReviewSystemExpActive =
    reviewSystemExp.showReviewSummary ||
    reviewSystemExp.showReviewFilterTags ||
    reviewSystemExp.showReviewSummaryAndFilterTags

  const selectedSortByOption =
    props.sortByOptions.find((option: SelectOptionProps) => option.value === props.sortBy)?.label ?? ''
  const formattedSortByLabel =
    isReviewSystemExpActive && selectedSortByOption ? `${t('sort_by')}${': ' + selectedSortByOption}` : t('sort_by')

  useEffect(() => {
    props.onSearchReview('')
  }, [props.onSearchReview])

  return (
    <div
      className={classnames(styles.filterWrap, className, {
        [styles.filterWrap_newPdpv2 ?? '']: props.newPdpv2,
        [styles.filterWrap_reviewSystemExp__noBorderTop ?? '']: isReviewSystemExpActive,
        [styles.filterWrap_showFilterTags__borderAdjustment__newPdpv2 ?? '']: props.newPdpv2 && isReviewSystemExpActive,
        [styles.filterWrap_showReviewFilterTags__fullWidth ?? '']: isReviewSystemExpActive,
      })}
    >
      <div className={isReviewSystemExpActive ? styles.filterWrap__spaceBetween : styles.filterWrap__left}>
        {isReviewSystemExpActive && (
          <ReviewFilterTags
            totalReviewsCount={props.totalReviewsCount}
            swiperWrapperClass={
              props.newPdpv2
                ? styles.filterWrap_swiperWrapperClass__adjustedPaddingLeft__newPdpv2
                : styles.filterWrap_swiperWrapperClass__adjustedPaddingLeft
            }
            filterOptions={props.filterOptions}
            setFilterBy={props.setFilterBy}
            filterBy={props.filterBy}
            productProperties={props.productProperties}
            setPage={props.setPage}
          />
        )}
        {!isReviewSystemExpActive && (
          <Select
            label={formattedSortByLabel}
            options={props.sortByOptions}
            onChange={props.setSortBy}
            className={classnames(styles.filterOptionItem, styles.selectBoxWrap)}
            labelWrapperClassName={styles.selectBoxLabel}
            variants={props.variants}
            hasPhoto={props.hasPhoto}
            productProperties={props.productProperties}
          />
        )}
        {isReviewSystemExpActive && !isMobile && (
          <Select
            label={formattedSortByLabel}
            options={props.sortByOptions}
            onChange={props.setSortBy}
            className={classnames(
              styles.filterOptionItem,
              styles.selectBoxWrap,
              styles.selectBoxWrapBorder,
              selectedSortByOption && styles.selectBoxWrapBorder_sortApplied,
              styles.filterOptionItemNewSpacing,
            )}
            labelWrapperClassName={classnames(styles.selectBoxLabel, styles.textTransformNone)}
            variants={props.variants}
            hasPhoto={props.hasPhoto}
            productProperties={props.productProperties}
            optionWrapperClassName={styles.selectOptionWrapper}
            trackNewSortEvent={isReviewSystemExpActive}
            labelClassName={styles.selectBoxLabelSpacing}
          />
        )}
        {!isReviewSystemExpActive && (
          <label className={classnames(styles.filterOptionItem, styles.photofilter)}>
            <span className={styles.photofilter__textAndInputWrapper}>{t('has_photo')} </span>
            <input
              data-testid="review-section-hasphoto"
              type="checkbox"
              // @i18n-ignore
              name="hasphoto"
              // eslint-disable-next-line i18next/no-literal-string
              value="yes"
              onChange={e => {
                props.setHasPhoto(e.target.checked)
              }}
            />
            <div className={styles.photofilter__checkbox}>
              {props.hasPhoto && <CrossIcon className={styles.photofilter__cross} />}
            </div>
          </label>
        )}
      </div>
      {isReviewSystemExpActive && isMobile && (
        <div
          className={classnames(styles.sortWrap, {
            [styles.sortWrap_newPdpv2 ?? '']: props.newPdpv2,
          })}
        >
          <p className={styles.reviewsCount}>
            {props.filterBy.count} {props.filterBy.count === 1 ? t('review_lowercase') : t('reviews')}
          </p>
          <Chip
            key={'sort_by'}
            label={formattedSortByLabel}
            onClick={() => {
              setIsSortDrawerOpen(true)
              trackEvent(TELEMETRY_EVENT_NAMES.SORT_OPENED, {
                context: {
                  // eslint-disable-next-line i18next/no-literal-string
                  contentContainerDisplayName: 'Product Rating & Reviews',
                  // eslint-disable-next-line i18next/no-literal-string
                  contentContainerName: 'Sort and Filter Options',
                  // eslint-disable-next-line i18next/no-literal-string
                  contentContainerType: 'Sort',
                  productProperties: props.productProperties,
                },
              })
            }}
            className={classnames(styles.chip, selectedSortByOption && styles.chip_sortApplied)}
          />
          <FilterDrawer
            placement="bottom"
            open={isSortDrawerOpen}
            onClose={() => {
              setIsSortDrawerOpen(false)
            }}
            title={t('sort_by')}
            height={'auto'}
            totalSelectedFilters={0}
            customFooter={null}
            isMobile={true}
            hideFooter={true}
            headerClassName={styles.sortDrawerHeader}
            zIndex={1010} // Ensure overlay appears above header (header sticky has z-index: 1002)
          >
            <div className={styles.sortDrawerContent}>
              <RadioButtonGroup
                name={'sort_by'}
                options={props.sortByOptions}
                defaultValue={props?.sortByOptions?.[0]?.value}
                onChange={value => {
                  props.setSortBy(value)
                  setIsSortDrawerOpen(false)
                  trackEvent(TELEMETRY_EVENT_NAMES.SORT_USED, {
                    context: {
                      sort_used: value,
                      // eslint-disable-next-line i18next/no-literal-string
                      contentContainerDisplayName: 'Product Rating & Reviews',
                      // eslint-disable-next-line i18next/no-literal-string
                      contentContainerName: 'Sort and Filter Options',
                      // eslint-disable-next-line i18next/no-literal-string
                      contentContainerType: 'Sort',
                      productProperties: props.productProperties,
                    },
                  })
                }}
                className={styles.sortRadioGroup}
                itemsClassName={styles.sortRadioItems}
                radioItemClassName={styles.sortRadioItem}
                radioLabelClassName={styles.sortRadioLabel}
              />
            </div>
          </FilterDrawer>
        </div>
      )}
    </div>
  )
}

export default ReviewsFilter
